import { type InputCapture } from '@canvas/input';

import { SCROLLABLE_INNER_CONTAINER_ID } from '../../effects/highlights/constants';
import { type SelectionsMachine } from '../../machine/selection-machine';
import { type SharedEventLoop } from '../event-loop';
import { type SelectionInput } from '../types';

import { type SelectionUiInteraction } from './selection-ui-interaction';
import { createMouseStateMachine } from './state-machine/mouse-state-machine';
import { getClickTarget } from './utils/get-click-target';

export type { SelectionUiInteraction } from './selection-ui-interaction';

export type MouseRowViewportBounds = {
  getMaxVisibleRowIndex: () => number;
};

export type CreateMouseInputOptions = {
  sharedLoop: SharedEventLoop;
  viewportBounds: MouseRowViewportBounds;
  getVisibleEntryCount: () => number;
  /**
   * When set, forwards selection UI telemetry from the mouse state machine to the caller.
   */
  onSelectionUiInteraction?: (event: SelectionUiInteraction) => void;
};

function getScrollContainer(): HTMLElement | null {
  return document.getElementById(SCROLLABLE_INNER_CONTAINER_ID);
}

export function createMouseInput(
  machine: SelectionsMachine,
  inputCapture: InputCapture,
  options: CreateMouseInputOptions
): SelectionInput {
  const { sharedLoop, viewportBounds, getVisibleEntryCount, onSelectionUiInteraction } =
    options;

  let currentState = createMouseStateMachine({
    machine,
    inputCapture,
    getClickTarget,
    getScrollContainer,
    getMaxVisibleRowIndex: viewportBounds.getMaxVisibleRowIndex,
    getVisibleEntryCount,
    onSelectionUiInteraction,
  });
  let unsubscribe: () => void;

  return {
    setup() {
      unsubscribe = sharedLoop.subscribe((events) => {
        for (const event of events) {
          if (event.type === 'pointerDown') {
            currentState = currentState.onPointerDown(event);
          } else if (event.type === 'pointerMove') {
            currentState = currentState.onPointerMove(event);
          } else if (event.type === 'pointerUp') {
            currentState = currentState.onPointerUp(event);
          }
        }
      });
    },

    dispose() {
      unsubscribe?.();
    },
  };
}
